import styled from 'styled-components';

export const PageContainer = styled.div`
  min-height: 100vh;
  background:
    radial-gradient(circle at top left, rgba(249, 228, 239, 0.9), transparent 32%),
    linear-gradient(180deg, #fffaf8 0%, #fff7f3 45%, #f7eef3 100%);
  color: #41233a;
`;

export const HeroSection = styled.section`
  min-height: clamp(30rem, 70svh, 44rem);
  padding: 2.5rem 1.25rem;
  display: flex;
  align-items: center;

  @media (min-width: 900px) {
    min-height: clamp(34rem, 76svh, 50rem);
    padding: 3rem 2.5rem;
  }
`;

export const HeroInner = styled.div`
  max-width: 980px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
`;

export const HeroCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  text-align: center;
`;

export const Eyebrow = styled.p`
  margin: 0;
  color: var(--aa-coral-deep);
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font: 700 0.78rem/1.2 'Lato', Arial, sans-serif;
`;

export const TitleLockup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
`;

export const TitleShell = styled.img`
  width: clamp(74px, 16vw, 128px);
  height: auto;
  display: block;
`;

export const Title = styled.h1`
  margin: 0;
  font-family: 'Robecha Daniera', Georgia, serif;
  font-size: clamp(2.35rem, 9vw, 5rem);
  font-weight: 400;
  line-height: 0.9;
  letter-spacing: 0.012em;
  color: var(--aa-coral);

  @media (min-width: 720px) {
    font-size: clamp(3.7rem, 7vw, 5.8rem);
  }
`;

export const Subtitle = styled.p`
  margin: 0;
  max-width: 28rem;
  color: var(--aa-sun);
  font: italic 300 clamp(1.05rem, 2.1vw, 1.3rem)/1.5 'Lato', Arial, sans-serif;
`;

export const HeroShopRail = styled.div`
  display: grid;
  gap: 0.75rem;
  margin-top: 0.25rem;
  justify-items: center;
`;

export const HeroShopLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.65rem;
`;

export const Section = styled.section<{ $tone?: 'blush' | 'sky' | 'sand' }>`
  min-height: clamp(26rem, 58svh, 40rem);
  padding: 2rem 1.5rem;
  display: flex;
  align-items: center;

  @media (min-width: 900px) {
    min-height: clamp(30rem, 64svh, 44rem);
    padding: 2.5rem 2.5rem;
  }

  ${({ $tone }) =>
    $tone === 'sky'
      ? 'background: linear-gradient(180deg, rgba(171, 190, 219, 0.16), rgba(255, 255, 255, 0));'
      : $tone === 'sand'
        ? 'background: linear-gradient(180deg, rgba(237, 224, 206, 0.26), rgba(255, 255, 255, 0));'
        : 'background: linear-gradient(180deg, rgba(249, 228, 239, 0.62), rgba(255, 247, 243, 0.92));'}
`;

export const SectionInner = styled.div`
  max-width: 1180px;
  width: 100%;
  margin: 0 auto;
`;

export const SectionHeading = styled.div`
  max-width: 42rem;
  margin-bottom: 1.8rem;

  h2 {
    margin: 0.3rem 0 0.9rem;
    color: #41233a;
    font: 400 clamp(2rem, 5vw, 3.5rem)/0.95 'Robecha Daniera', Georgia, serif;
  }

  p {
    margin: 0;
    color: #5f4a56;
    font: 300 1rem/1.75 'Lato', Arial, sans-serif;
  }
`;

export const StoryGrid = styled.div`
  display: grid;
  gap: 1rem;

  @media (min-width: 900px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

export const StoryCard = styled.article`
  min-height: 220px;
  padding: 1.35rem;
  border-radius: 26px;
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid rgba(199, 67, 135, 0.1);
  box-shadow: 0 16px 36px rgba(84, 56, 74, 0.08);

  h3 {
    margin: 0 0 0.8rem;
    color: var(--aa-coral);
    font: 400 1.65rem/1.05 'Robecha Daniera', Georgia, serif;
  }

  p {
    margin: 0;
    color: #5f4a56;
    font: 300 0.98rem/1.7 'Lato', Arial, sans-serif;
  }
`;

export const PathwayGrid = styled.div`
  display: grid;
  gap: 1rem;

  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
    align-items: start;
  }
`;

export const PathwayPanel = styled.div`
  padding: 1.4rem;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(90, 56, 79, 0.1);
  box-shadow: 0 18px 36px rgba(84, 56, 74, 0.08);

  h3 {
    margin: 0 0 0.7rem;
    color: #41233a;
    font: 400 2rem/1 'Robecha Daniera', Georgia, serif;
  }

  p {
    margin: 0 0 1rem;
    color: #5f4a56;
    font: 300 0.98rem/1.7 'Lato', Arial, sans-serif;
  }
`;

export const SocialLinksContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.9rem;
`;

export const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.85rem 1.05rem;
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(199, 67, 135, 0.1), rgba(249, 228, 239, 0.74));
  color: #41233a;
  text-decoration: none;
  font: 700 0.92rem/1 'Lato', Arial, sans-serif;
  transition: transform 180ms ease, box-shadow 180ms ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 28px rgba(92, 55, 79, 0.12);
  }
`;

export const HeroSocialLink = styled(SocialLink)`
  padding: 0.72rem 0.95rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(199, 67, 135, 0.12);
  box-shadow: none;
  font-size: 0.84rem;

  &:hover {
    box-shadow: 0 12px 24px rgba(92, 55, 79, 0.1);
  }
`;

export const LinkIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.8);
  color: var(--aa-coral);

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

export const FeatureList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.7rem;

  li {
    padding: 0.9rem 1rem;
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.76);
    color: #5f4a56;
    font: 300 0.96rem/1.6 'Lato', Arial, sans-serif;
    border: 1px solid rgba(171, 190, 219, 0.28);
  }
`;
